/**
 * F606UsersAdmin — user moderation panel
 *
 * Actions per row:
 *   • Ban        (with reason + duration)
 *   • Unban
 *   • Notify     (title + body + channels)
 *   • Gift subscription (plan + duration)
 *
 * Trainer/trainee profile fields are read from `trainer_profile` /
 * `trainee_profile` on the user document (as documented by the 606 team).
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Ban, CheckCircle2, Bell, Gift, Search } from 'lucide-react';
import { bridge, describeActions } from './bridgeClient';

export default function F606UsersAdmin({ language }) {
  const isAr = language === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await bridge.list('users', { limit: 100, search: search || undefined });
    if (res.success !== false) setRows(res.data || []);
    else toast.error(res.error?.message || 'Failed to load users');
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const act = async (user, fn, label, body = {}) => {
    setBusy((s) => ({ ...s, [user.id]: label }));
    const res = await fn(user.id, body);
    setBusy((s) => ({ ...s, [user.id]: null }));
    if (res.success) {
      toast.success(`✅ ${label} · ${describeActions(res.actions_fired, language)}`, { duration: 5000 });
      load();
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="f606-users-admin">
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <Search className="absolute top-2.5 start-3 h-4 w-4 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'ابحث بالاسم/البريد/الهاتف...' : 'Search name/email/phone...'}
            className="w-full ps-9 pe-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30"
          />
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'المستخدم' : 'User'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'الإيميل' : 'Email'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'الحالة' : 'Status'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'الإجراءات' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && rows.length === 0 && (
              <tr><td colSpan="4" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="4" className="text-center py-8 text-white/40">{isAr ? 'لا يوجد مستخدمون' : 'No users'}</td></tr>
            )}
            {rows.map((u) => {
              const bId = busy[u.id];
              const banned = u.is_banned;
              return (
                <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`f606-user-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <div className="text-white font-medium">{u.full_name || u.name || '—'}</div>
                    <div className="text-[10px] text-white/40 font-mono" dir="ltr">{u.phone || '—'}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/70" dir="ltr">{u.email || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {banned ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-red-500/15 border-red-500/40 text-red-300">
                        {isAr ? 'محظور' : 'Banned'}
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-emerald-500/15 border-emerald-500/40 text-emerald-300">
                        {isAr ? 'نشط' : 'Active'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {!banned && (
                        <button
                          disabled={bId}
                          onClick={() => {
                            const reason = window.prompt(isAr ? 'سبب الحظر:' : 'Ban reason:') || '';
                            if (!reason) return;
                            const days = Number(window.prompt(isAr ? 'مدّة الحظر (أيّام):' : 'Duration (days):') || '30');
                            act(u, bridge.user.ban, isAr ? 'حظر' : 'Ban', { reason, duration_days: days });
                          }}
                          className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-user-ban-${u.id}`}
                        >
                          <Ban className="h-3 w-3 inline" /> {isAr ? 'حظر' : 'Ban'}
                        </button>
                      )}
                      {banned && (
                        <button
                          disabled={bId}
                          onClick={() => act(u, bridge.user.unban, isAr ? 'فكّ الحظر' : 'Unban')}
                          className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-user-unban-${u.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3 inline" /> {isAr ? 'فكّ حظر' : 'Unban'}
                        </button>
                      )}
                      <button
                        disabled={bId}
                        onClick={() => {
                          const title = window.prompt(isAr ? 'عنوان الإشعار:' : 'Title:') || '';
                          if (!title) return;
                          const body = window.prompt(isAr ? 'المحتوى:' : 'Body:') || '';
                          if (!body) return;
                          act(u, bridge.user.notify, isAr ? 'إشعار' : 'Notify', {
                            title, body, channels: ['push', 'email'],
                          });
                        }}
                        className="px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 text-[10px] font-bold disabled:opacity-40"
                        data-testid={`f606-user-notify-${u.id}`}
                      >
                        <Bell className="h-3 w-3 inline" /> {isAr ? 'إشعار' : 'Notify'}
                      </button>
                      <button
                        disabled={bId}
                        onClick={() => {
                          const plan = window.prompt(isAr ? 'الخطّة (premium_monthly...):' : 'Plan (premium_monthly...):') || 'premium_monthly';
                          const days = Number(window.prompt(isAr ? 'المدّة (أيّام):' : 'Duration (days):') || '30');
                          if (!plan || !days) return;
                          act(u, bridge.user.giftSubscription, isAr ? 'هديّة' : 'Gift', {
                            plan, duration_days: days,
                          });
                        }}
                        className="px-2 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 text-[10px] font-bold disabled:opacity-40"
                        data-testid={`f606-user-gift-${u.id}`}
                      >
                        <Gift className="h-3 w-3 inline" /> {isAr ? 'هديّة' : 'Gift'}
                      </button>
                      {bId && <Loader2 className="h-3 w-3 animate-spin text-emerald-400/70" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
